import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import CONSTANTS from 'utils/constants'
import type { SentryEnv } from '../../vite-env.d.ts'
import { isAbortError, isApiError } from './apiError'
import { safeResourceUrl } from './safeUrl'

/** Matches the Birdie widget script loaded from `index.html`. See ROST-3802. */
export const BIRDIE_WIDGET_URL_PATTERN = /^https:\/\/app\.birdie\.so\//

/** Matches Chargebee's hosted-fields SDK, also loaded from `index.html`. See ROST-3804. */
export const CHARGEBEE_SDK_URL_PATTERN = /^https:\/\/js\.chargebee\.com\//

/**
 * Sentry's own global unhandledrejection handler falls back to this message
 * when a rejection reaches it with neither a `reason` nor a `detail.reason`
 * (see `_getUnhandledRejectionError` in
 * `@sentry/browser/esm/integrations/globalhandlers.js`, which blames "likely
 * a browser extension" mangling the native `PromiseRejectionEvent` -
 * https://github.com/getsentry/sentry-javascript/issues/2380). Every
 * occurrence we've seen is Safari, with no stack and no keys - there is no
 * application code in the path. See ROST-3803.
 */
export const EXTENSION_MANGLED_REJECTION_PATTERN =
  /Object captured as promise rejection with keys: \[object has no keys\]/

/**
 * Initialise sentry, used for
 * - Crash analytics
 * - Performance monitoring
 * - Debugging
 */
export const initialiseSentry = () => {
  const sentryEnv = CONSTANTS.VITE_SENTRY_ENV
  const { tracesSampleRate } = getEnvProps(sentryEnv)

  Sentry.init({
    enabled: CONSTANTS.VITE_SENTRY_ENABLED,
    dsn: 'https://b7b7bcf682aa4790a25d3bebc0f46731@o1072246.ingest.sentry.io/6070751',
    beforeSend(event, hint) {
      if (shouldDropEvent(hint?.originalException)) {
        return null
      }

      // CM-4459: a DOM Event carries no type, message or stack, so Sentry can
      // only report one as `<unknown>`. Attach what identifies the resource
      // that actually failed. Grouping is left alone on purpose - see
      // `domEventContext`.
      const domEvent = domEventContext(hint?.originalException)
      if (domEvent) {
        event.extra = { ...event.extra, domEvent }
        event.tags = { ...event.tags, domEventSource: domEventSource(domEvent) }
      }

      // Get the URL from the event
      const url = event.request?.url

      // Same privacy contract as the DOM resource URLs below: origin and path
      // only. Redacting ObjectIds alone left the query string and fragment
      // intact, so a page reached with `?token=…` shipped the token to Sentry.
      if (url && event.request) {
        event.request.url = safeResourceUrl(url)
      }

      return event
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ],

    // ROST-3802: Birdie's embedded widget (index.html) throws its own unhandled
    // "Failed to fetch" from its bootstrap request. Every frame is inside
    // app.birdie.so's bundle, so it's never something our code can fix - drop it
    // at the source rather than let it page us as a care-office crash.
    // ROST-3804: Chargebee's hosted-fields SDK (also loaded globally from
    // index.html) throws "Could not mount master component" when its iframe
    // can't initialise - e.g. Safari ITP or a firewall blocking its domain.
    // Every frame is inside Chargebee's own bundle.
    denyUrls: [BIRDIE_WIDGET_URL_PATTERN, CHARGEBEE_SDK_URL_PATTERN],

    // ROST-3803: see EXTENSION_MANGLED_REJECTION_PATTERN - a Safari-extension
    // artifact with no stack and no application code involved.
    ignoreErrors: [EXTENSION_MANGLED_REJECTION_PATTERN],

    // tracesSampleRate at 1.0 to captures 100%
    // of transactions for performance monitoring
    // in our development environment
    tracesSampleRate,
    normalizeDepth: 10,
    environment: sentryEnv,
    release: CONSTANTS.VITE_RELEASE_NAME
  })

  return Sentry.withSentryReactRouterV6Routing(Routes)
}

type EnvProps = {
  tracesSampleRate: number
}
const getEnvProps = (env: SentryEnv): EnvProps => {
  const tracesSampleRate = getTracesSampleRate(env)

  return {
    tracesSampleRate
  }
}

const getTracesSampleRate = (env: SentryEnv): number => {
  switch (env) {
    case 'Dev':
      return 0
    case 'Staging':
      return 0.1
    case 'Prod':
      return 0.01
    default:
      return 0
  }
}

export type SentryFeatureArea = 'emar' | 'rota' | 'careManagement'

/**
 * Rota/rostering screens (from `screens/Rostering`) that are mounted outside
 * the `rota/*` prefix — under `settings/`, `residents/:id/settings/`,
 * `organisation/team/:id/`, and `reports/rostering/*` — because the module
 * boundary doesn't match the URL structure. Checked as exact path segments.
 */
const ROTA_PATH_SEGMENTS = [
  'rostering',
  'care-package',
  'care-package-and-patterns',
  'contracts-and-absences',
  'contract-and-preferences',
  'warnings',
  'rota-patterns',
  'travel-time-and-mileage',
  'geofencing',
  'pay-rates',
  'finance',
  'absences',
  'all-absences'
]

/**
 * Maps the current route to a coarse feature area so Sentry issues can be
 * filtered by product area. Emar routes are nested under `residents/:id/...`
 * rather than being a sibling of it (including a nested `emar-activation`
 * paywall route distinct from the top-level one), so they're matched before
 * the careManagement fallback, which covers everything else in the app.
 */
export function getFeatureAreaFromPathname(
  pathname: string
): SentryFeatureArea {
  const segments = pathname.replace(/^\/+/, '').split('/')

  if (segments[0] === 'rota') return 'rota'
  if (ROTA_PATH_SEGMENTS.some((segment) => segments.includes(segment))) {
    return 'rota'
  }
  if (segments.includes('emar-activation') || segments.includes('emar')) {
    return 'emar'
  }

  return 'careManagement'
}

/**
 * Drops 404s and aborted requests. A 404 belongs in the backend's logs rather
 * than in frontend crash reporting (CM-2340), and an abort is a request we
 * cancelled ourselves.
 */
export function shouldDropEvent(originalException: unknown): boolean {
  if (isAbortError(originalException)) return true

  return isApiError(originalException) && originalException.status === 404
}

/**
 * What we can learn from a DOM `Event` that reached Sentry as a thrown value or
 * an unhandled rejection. Sentry reports these as `<unknown>` because an Event
 * has no type, message or stack of its own, so nothing in the issue says which
 * resource failed (CM-4459).
 *
 * Deliberately does NOT set a fingerprint. Grouping stays as Sentry has it so
 * the existing issue keeps its history instead of shattering into one issue per
 * failing url.
 *
 * Returns `undefined` for anything that is not an Event, so every other
 * exception passes through `beforeSend` untouched.
 */
export type DomEventContext = {
  /** Tells a `ProgressEvent` from a plain `Event` or a CSP violation. */
  eventConstructor: string | undefined
  eventType: string
  targetConstructor: string | undefined
  targetTagName: string | undefined
  targetUrl: string | undefined
  /** A failed XHR's own status. 0 means it never completed at all. */
  targetStatus: number | undefined
  currentTargetDiffers: boolean
}

export function domEventContext(
  originalException: unknown
): DomEventContext | undefined {
  // `Event` is absent outside a browser/jsdom, so check before `instanceof`.
  if (typeof Event === 'undefined' || !(originalException instanceof Event)) {
    return undefined
  }

  const { target, currentTarget } = originalException
  const targetObject = objectOrUndefined(target)

  return {
    eventConstructor: constructorName(originalException),
    eventType: originalException.type,
    targetConstructor:
      targetObject === undefined ? undefined : constructorName(targetObject),
    targetTagName: stringField(target, 'tagName'),
    targetUrl: targetResourceUrl(target),
    targetStatus: numberField(target, 'status'),
    // Usually true: the DOM nulls `currentTarget` once dispatch is over, and
    // `beforeSend` runs long after. Worth having when it is not null.
    currentTargetDiffers: currentTarget !== target
  }
}

/** Low-cardinality label so the Sentry UI can aggregate by failing source. */
export const domEventSource = (context: DomEventContext): string =>
  context.targetTagName ?? context.targetConstructor ?? 'unknown'

/**
 * A resource-error Event's `target` is the thing that failed: an `Element` for a
 * blocked script, image or stylesheet, or the `XMLHttpRequest` for a failed
 * request. Neither is guaranteed, and an Element need not have a `tagName`, so
 * every field is probed rather than assumed.
 */
function targetResourceUrl(target: unknown): string | undefined {
  const raw =
    stringField(target, 'src') ??
    stringField(target, 'href') ??
    stringField(target, 'responseURL')

  return raw === undefined ? undefined : safeResourceUrl(raw)
}

const objectOrUndefined = (value: unknown): object | undefined =>
  typeof value === 'object' && value !== null ? value : undefined

const constructorName = (value: object): string | undefined => {
  const ctor = (value as { constructor?: { name?: unknown } }).constructor
  return typeof ctor?.name === 'string' && ctor.name ? ctor.name : undefined
}

/** Reads one property off a loosely-typed target without trusting its shape. */
const field = (value: unknown, name: string): unknown =>
  objectOrUndefined(value) === undefined
    ? undefined
    : (value as Record<string, unknown>)[name]

const stringField = (value: unknown, name: string): string | undefined => {
  const found = field(value, name)
  return typeof found === 'string' && found ? found : undefined
}

const numberField = (value: unknown, name: string): number | undefined => {
  const found = field(value, name)
  return typeof found === 'number' ? found : undefined
}

export default initialiseSentry
